import DOMPurify from 'dompurify'
import { cn } from '@/shared/lib/utils'

export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'strong',
  'em',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'br',
  'sub',
  'sup',
  'span',
  'mark',
  'blockquote',
  'code',
  'pre',
]

export const RICH_TEXT_ALLOWED_ATTRS = ['href', 'src', 'alt', 'style', 'target', 'rel', 'class']

export const sanitizeRichTextHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTRS,
  })

// Plain-text preview of rich-text HTML for space-constrained contexts (table cells, tooltips).
// Strips tags via a sanitized-parse pass, then reads textContent so HTML entities decode correctly.
export const richTextToPlainText = (html: string): string => {
  if (!html) return ''
  const template = document.createElement('template')
  template.innerHTML = sanitizeRichTextHtml(html)
  return (template.content.textContent ?? '').replace(/\s+/g, ' ').trim()
}

// True when the markup renders something visible. A plain-text check alone is not enough:
// notes whose only content is an image (REBUILD-359) have no text but must still be shown.
export const hasRichTextContent = (html: string | null | undefined): boolean => {
  if (!html) return false
  const sanitized = sanitizeRichTextHtml(html)
  if (!sanitized.trim()) return false

  const template = document.createElement('template')
  template.innerHTML = sanitized
  if ((template.content.textContent ?? '').trim().length > 0) return true

  return template.content.querySelector('img') != null
}

export const RICH_TEXT_DISPLAY_CLASSNAME = cn(
  // `rich-text-content` carries the legacy Quill `ql-*` rules (see styles/globals.css).
  'rich-text-content prose prose-sm max-w-none text-sm break-words',
  '[&_img]:max-w-full [&_img]:rounded',
  '[&_mark]:rounded-sm',
  '[&_a]:break-all',
)
